import * as THREE from 'three';
import { EnemyType } from './EnemyType';
import { HealthSystem } from '../systems/HealthSystem';
import { NavigationSystem } from '../systems/NavigationSystem';
import { HealthbarSystem } from '../systems/HealthbarSystem';

/**
 * Base class for all enemies in the game
 */
export class Enemy {
  // Core properties
  protected object: THREE.Object3D;
  protected model: THREE.Mesh;
  protected type: EnemyType;
  protected scene: THREE.Scene;
  protected id: string;
  
  // Systems
  protected healthSystem: HealthSystem;
  protected navigationSystem: NavigationSystem;
  protected healthbarSystem: HealthbarSystem;
  
  // State
  protected isActive: boolean = false;
  protected isDead: boolean = false;
  protected isAttacking: boolean = false;
  
  // Movement
  protected speed: number = 1.5; // Default value
  protected baseSpeed: number = 1.5; // Store original speed for slow effects
  protected position: THREE.Vector3;
  protected targetPosition: THREE.Vector3 | null = null;
  protected velocity: THREE.Vector3 = new THREE.Vector3();
  
  // Combat
  protected damage: number = 10; // Default value
  protected attackRange: number = 1.5; // Default value
  protected attackCooldown: number = 2.0; // Default value
  protected lastAttackTime: number = 0;
  
  // Status effects
  protected statusEffects: {
    type: string;
    damage: number;
    duration: number;
    startTime: number;
  }[] = [];
  protected slowEffect: {
    strength: number;
    duration: number;
    startTime: number;
  } | null = null;
  
  // Animation
  protected animationState: string = 'idle';
  protected animationTime: number = 0;
  
  /**
   * Creates a new enemy
   * @param type The type of enemy
   * @param position The initial position
   * @param scene The scene to add the enemy to
   */
  constructor(type: EnemyType, position: THREE.Vector3, scene: THREE.Scene) {
    this.type = type;
    this.position = position.clone();
    this.scene = scene;
    
    // Generate unique ID
    this.id = `enemy_${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    
    // Create container object
    this.object = new THREE.Object3D();
    this.object.position.copy(position);
    
    // Set default properties based on type
    this.setPropertiesByType();
    
    // Initialize systems
    this.healthSystem = new HealthSystem(this.getInitialMaxHealth());
    
    // Create model
    this.model = this.createModel();
    this.object.add(this.model);
    
    // Initialize navigation system
    this.navigationSystem = new NavigationSystem();
    
    // Initialize healthbar system
    this.healthbarSystem = new HealthbarSystem(this.healthSystem, this.object, this.getHeight());
    
    // Store base speed
    this.baseSpeed = this.speed;
    
    // Add to scene
    scene.add(this.object);
  }
  
  /**
   * Sets properties based on enemy type
   */
  protected setPropertiesByType(): void {
    switch (this.type) {
      case EnemyType.GRUNT:
        this.speed = 2.0;
        this.damage = 10;
        this.attackRange = 1.5;
        this.attackCooldown = 2.0;
        break;
        
      case EnemyType.ARCHER:
        this.speed = 1.5;
        this.damage = 15;
        this.attackRange = 10.0;
        this.attackCooldown = 3.0;
        break;
        
      case EnemyType.BRUTE:
        this.speed = 1.0;
        this.damage = 25;
        this.attackRange = 2.0;
        this.attackCooldown = 3.5;
        break;
        
      case EnemyType.BOSS:
        this.speed = 0.8;
        this.damage = 40;
        this.attackRange = 3.0;
        this.attackCooldown = 5.0;
        break;
        
      default:
        this.speed = 1.5;
        this.damage = 10;
        this.attackRange = 1.5;
        this.attackCooldown = 2.0;
        break;
    }
  }
  
  /**
   * Creates the visual model for the enemy
   * @returns The enemy mesh
   */
  protected createModel(): THREE.Mesh {
    let geometry: THREE.BufferGeometry;
    let material: THREE.Material;
    
    // Create different models based on enemy type
    switch (this.type) {
      case EnemyType.GRUNT:
        geometry = new THREE.BoxGeometry(0.8, 1.8, 0.8);
        material = new THREE.MeshStandardMaterial({ color: 0xcc0000 });
        break;
        
      case EnemyType.ARCHER:
        geometry = new THREE.ConeGeometry(0.5, 1.8, 4);
        material = new THREE.MeshStandardMaterial({ color: 0x00cc00 });
        break;
        
      case EnemyType.BRUTE:
        geometry = new THREE.BoxGeometry(1.2, 2.2, 1.2);
        material = new THREE.MeshStandardMaterial({ color: 0x0000cc });
        break;
        
      case EnemyType.BOSS:
        geometry = new THREE.SphereGeometry(1.5, 16, 16);
        material = new THREE.MeshStandardMaterial({ color: 0xcc00cc });
        break;
        
      default:
        geometry = new THREE.BoxGeometry(1, 2, 1);
        material = new THREE.MeshStandardMaterial({ color: 0xcccccc });
        break;
    }
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    // Position mesh so its bottom is at y=0
    mesh.position.y = this.getHeight() / 2;
    
    return mesh;
  }
  
  /**
   * Gets the initial maximum health based on enemy type
   * Used during initialization
   * @returns The maximum health
   */
  protected getInitialMaxHealth(): number {
    switch (this.type) {
      case EnemyType.GRUNT:
        return 50;
      case EnemyType.ARCHER:
        return 30;
      case EnemyType.BRUTE:
        return 100;
      case EnemyType.BOSS:
        return 300;
      default:
        return 50;
    }
  }
  
  /**
   * Gets the height of the enemy
   * @returns The height
   */
  public getHeight(): number {
    switch (this.type) {
      case EnemyType.GRUNT:
        return 1.8;
      case EnemyType.ARCHER:
        return 1.8;
      case EnemyType.BRUTE:
        return 2.2;
      case EnemyType.BOSS:
        return 3.0;
      default:
        return 2.0;
    }
  }
  
  /**
   * Activates the enemy
   */
  public activate(): void {
    this.isActive = true;
    this.animationState = 'idle';
  }
  
  /**
   * Deactivates the enemy
   */
  public deactivate(): void {
    this.isActive = false;
  }
  
  /**
   * Sets the target position for the enemy to move towards
   * @param position The target position
   */
  public setTargetPosition(position: THREE.Vector3): void {
    this.targetPosition = position.clone();
    
    // Calculate path to target
    if (this.navigationSystem) {
      this.navigationSystem.calculatePath(this.position, this.targetPosition);
    }
  }
  
  /**
   * Makes the enemy take damage
   * @param amount The amount of damage
   * @returns True if the enemy died from this damage
   */
  public takeDamage(amount: number): boolean {
    if (this.isDead) return false;
    
    const isDead = this.healthSystem.takeDamage(amount);
    
    if (isDead) {
      this.die();
      return true;
    }
    
    // Play hit animation/effect
    this.animationState = 'hit';
    this.animationTime = 0;
    
    return false;
  }
  
  /**
   * Handles enemy death
   */
  protected die(): void {
    this.isDead = true;
    this.isActive = false;
    this.animationState = 'death';
    this.animationTime = 0;
    
    // Hide healthbar
    if (this.healthbarSystem) {
      this.healthbarSystem.hide();
    }
    
    // Play death animation/effect
    // This will be implemented in derived classes
  }
  
  /**
   * Makes the enemy attack
   */
  public attack(): void {
    if (this.isDead || !this.isActive) return;
    
    const currentTime = performance.now() / 1000;
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return; // Still on cooldown
    }
    
    this.isAttacking = true;
    this.animationState = 'attack';
    this.animationTime = 0;
    this.lastAttackTime = currentTime;
    
    // Attack logic will be implemented in derived classes
  }
  
  /**
   * Updates the enemy
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    if (!this.isActive || this.isDead) return;
    
    // Set player position as target
    this.setTargetPosition(playerPosition);
    
    // Update animation time
    this.animationTime += deltaTime;
    
    // Update status effects
    this.updateStatusEffects(deltaTime);
    
    // Update movement
    this.updateMovement(deltaTime);
    
    // Update healthbar
    if (this.healthbarSystem) {
      this.healthbarSystem.update();
    }
    
    // Check if should attack
    if (this.targetPosition && this.position.distanceTo(this.targetPosition) <= this.attackRange) {
      this.attack();
    }
  }
  
  /**
   * Updates the enemy's movement
   * @param deltaTime Time since last update in seconds
   */
  protected updateMovement(deltaTime: number): void {
    // Update target to follow player if not attacking
    if (!this.isAttacking && this.targetPosition) {
      // Calculate direction to target
      const direction = new THREE.Vector3().subVectors(this.targetPosition, this.position).normalize();
      
      // Calculate velocity
      this.velocity.copy(direction).multiplyScalar(this.speed);
      
      // Update position
      this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
      this.object.position.copy(this.position);
      
      // Rotate to face direction of movement
      if (direction.length() > 0.001) {
        const targetRotation = Math.atan2(direction.x, direction.z);
        this.object.rotation.y = targetRotation;
      }
    }
  }
  
  /**
   * Updates the animation state
   * @param deltaTime Time since last update in seconds
   */
  protected updateAnimationState(deltaTime: number): void {
    // Handle animation state transitions
    switch (this.animationState) {
      case 'hit':
        if (this.animationTime > 0.5) {
          this.animationState = 'idle';
          this.animationTime = 0;
        }
        break;
        
      case 'attack':
        if (this.animationTime > 1.0) {
          this.isAttacking = false;
          this.animationState = 'idle';
          this.animationTime = 0;
        }
        break;
        
      case 'death':
        if (this.animationTime > 2.0) {
          // Remove from scene after death animation
          if (this.object.parent) {
            this.object.parent.remove(this.object);
          }
        }
        break;
    }
  }
  
  /**
   * Gets the enemy's position
   * @returns The position
   */
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }
  
  /**
   * Gets the enemy's object
   * @returns The object
   */
  public getObject(): THREE.Object3D {
    return this.object;
  }
  
  /**
   * Gets the enemy's type
   * @returns The type
   */
  public getType(): EnemyType {
    return this.type;
  }
  
  /**
   * Gets the enemy's health
   * @returns The current health
   */
  public getHealth(): number {
    return this.healthSystem.getCurrentHealth();
  }
  
  /**
   * Gets the enemy's max health
   * @returns The max health
   */
  public getMaxHealth(): number {
    return this.healthSystem.getMaxHealth();
  }
  
  /**
   * Checks if the enemy is dead
   * @returns True if dead
   */
  public isDying(): boolean {
    return this.isDead;
  }
  
  /**
   * Checks if the enemy is active
   * @returns True if active
   */
  public isActiveEnemy(): boolean {
    return this.isActive;
  }
  
  /**
   * Makes the healthbar face the camera
   * @param camera The camera to face
   */
  public updateHealthbarFacing(camera: THREE.Camera): void {
    if (this.healthbarSystem) {
      this.healthbarSystem.faceCamera(camera);
    }
  }
  
  /**
   * Updates the enemy's internal position from the object's position
   * Used after collision response to ensure internal state matches visual position
   */
  public updatePositionFromObject(): void {
    this.position.copy(this.object.position);
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    // Remove from scene
    if (this.object.parent) {
      this.object.parent.remove(this.object);
    }
    
    // Dispose of model resources
    if (this.model) {
      if (this.model.geometry) {
        this.model.geometry.dispose();
      }
      
      if (this.model.material) {
        if (Array.isArray(this.model.material)) {
          this.model.material.forEach(material => material.dispose());
        } else {
          this.model.material.dispose();
        }
      }
    }
    
    // Dispose of healthbar
    if (this.healthbarSystem) {
      this.healthbarSystem.dispose();
    }
  }
  
  /**
   * Gets the enemy's unique ID
   * @returns The unique ID
   */
  public getId(): string {
    return this.id;
  }
  
  /**
   * Checks if the enemy is alive
   * @returns True if alive
   */
  public isAlive(): boolean {
    return !this.isDead;
  }
  
  /**
   * Applies damage over time to the enemy
   * @param damage The damage per second
   * @param duration The duration in seconds
   * @param type The type of damage (fire, poison, etc.)
   */
  public applyDamageOverTime(damage: number, duration: number, type: string): void {
    // Add new damage over time effect
    this.statusEffects.push({
      type,
      damage,
      duration,
      startTime: performance.now() / 1000
    });
    
    console.log(`Applied ${type} damage over time to enemy ${this.id}: ${damage} damage/sec for ${duration} seconds`);
  }
  
  /**
   * Applies a slow effect to the enemy
   * @param strength The strength of the slow effect (0-1)
   * @param duration The duration in seconds
   */
  public applySlowEffect(strength: number, duration: number): void {
    // Apply slow effect
    this.slowEffect = {
      strength,
      duration,
      startTime: performance.now() / 1000
    };
    
    // Reduce speed
    this.speed = this.baseSpeed * (1 - strength);
    
    console.log(`Applied slow effect to enemy ${this.id}: ${(strength * 100).toFixed(0)}% for ${duration} seconds`);
  }
  
  /**
   * Updates status effects
   * @param deltaTime Time since last update in seconds
   */
  protected updateStatusEffects(deltaTime: number): void {
    const currentTime = performance.now() / 1000;
    
    // Update damage over time effects
    for (let i = this.statusEffects.length - 1; i >= 0; i--) {
      const effect = this.statusEffects[i];
      const elapsedTime = currentTime - effect.startTime;
      
      // Apply damage
      if (elapsedTime <= effect.duration) {
        const damageThisTick = effect.damage * deltaTime;
        this.takeDamage(damageThisTick);
      } else {
        // Remove expired effect
        this.statusEffects.splice(i, 1);
      }
    }
    
    // Update slow effect
    if (this.slowEffect) {
      const elapsedTime = currentTime - this.slowEffect.startTime;
      
      if (elapsedTime > this.slowEffect.duration) {
        // Remove slow effect
        this.speed = this.baseSpeed;
        this.slowEffect = null;
      }
    }
  }
} 